'use client'

import { createContext, useContext, useEffect, useState, useRef, useCallback, type ReactNode } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { api, BACKEND_URL, getAccessToken, setAccessToken, refreshAccessToken, getLastRefreshErrorCode } from '@/lib/api'
import { companyQueryOptions } from '@/hooks/useCompany'
import { currentTeamMemberQueryOptions } from '@/hooks/useCurrentTeamMember'
import { reinitSupabase, clearRealtimeConfig } from '@/lib/supabase'
import rocketChat from '@shared/rocketchat-service'
import ChangePasswordDialog from '@/components/ChangePasswordDialog'
import { setChatUnavailable } from '@/lib/chat-unavailable-store'

// ─── Types ────────────────────────────────────────────────────────────────────
type AuthUser = {
  id: string
  email: string
  firstName?: string | null
  lastName?: string | null
  avatar?: string | null
  role?: string | null
  gender?: string | null
  colorHex?: string | null
  teamMemberId?: string | null
  mustChangePassword?: boolean
  permissions?: string[]
  accessGroupId?: string | null
}

type AuthContextType = {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string, rememberMe?: boolean, turnstileToken?: string) => Promise<void>
  logout: (reason?: string) => void
  adminModeEnabled: boolean
  setAdminModeEnabled: (enabled: boolean) => void
}

// ─── Constants ────────────────────────────────────────────────────────────────
// QA WEB-003 (Tony, 2026-09-18): the access token is NEVER persisted — it
// lives only in the api.ts module's memory (getAccessToken/setAccessToken).
// USER_KEY/EXPIRES_KEY are non-secret: just the cached profile + a timestamp
// used to schedule the proactive refresh timer, never anything that alone
// can authenticate a request. The refresh token itself never reaches the
// browser at all for web — see backend/src/routes/auth.ts (httpOnly
// `jm_refresh` cookie) and web/src/lib/api.ts.
const USER_KEY = 'jm_user'
const EXPIRES_KEY = 'jm_expires_at'
const ADMIN_MODE_KEY = 'jm_admin_mode'
// Pre-deploy localStorage keys — read once for the legacy migration / cleanup
// only (item 8), never written again. jm_token is not read directly by this
// file (it's api.ts's in-memory token now) but old sessions may still have it.
const LEGACY_TOKEN_KEY = 'jm_token'
const LEGACY_REFRESH_KEY = 'jm_refresh_token'
const IDLE_WARNING_MS = 60_000        // 60 seconds warning before auto-logout
const REFRESH_BUFFER_MS = 5 * 60_000  // refresh 5 minutes before expiry

// ─── Context ──────────────────────────────────────────────────────────────────
const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  // When set (to the temp password just used), force a mandatory password change.
  const [forceChangePwd, setForceChangePwd] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [adminModeEnabled, setAdminModeRaw] = useState(false)

  const router = useRouter()
  const queryClient = useQueryClient()
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const realtimeRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const idleTimeoutMs = useRef(60 * 60_000) // default 60 min — overridden by tenant setting

  // ── Admin mode persistence ──────────────────────────────────────────────────
  const setAdminModeEnabled = useCallback((enabled: boolean) => {
    setAdminModeRaw(enabled)
    if (typeof window !== 'undefined') {
      localStorage.setItem(ADMIN_MODE_KEY, String(enabled))
    }
  }, [])

  // ── Logout ──────────────────────────────────────────────────────────────────
  const logout = useCallback((reason?: string) => {
    // Kill all in-flight React Query fetches immediately — prevents the
    // re-render cascade from triggering stale refetches during unmount.
    queryClient.clear()

    // Call backend logout (fire-and-forget). credentials:'include' +
    // x-jm-client so the backend can clear the httpOnly jm_refresh cookie.
    const token = getAccessToken()
    if (token) {
      fetch(`${BACKEND_URL}/api/mvp-auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}`, 'x-jm-client': 'web' }
      }).catch(() => { /* ignore */ })
    }

    // Clear in-memory access token + local storage
    setAccessToken(null)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(EXPIRES_KEY)
    localStorage.removeItem(LEGACY_TOKEN_KEY)
    localStorage.removeItem(LEGACY_REFRESH_KEY)
    localStorage.removeItem('rc_authToken')
    localStorage.removeItem('rc_userId')
    localStorage.removeItem('jm_no_auto_timeout')
    try { sessionStorage.removeItem('jm_temp_pwd') } catch { /* ignore */ }
    setForceChangePwd(null)
    setUser(null)
    clearRealtimeConfig()

    // Disconnect RC WebSocket
    rocketChat.disconnectWebSocket()

    // Clear timers
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    if (realtimeRefreshRef.current) clearInterval(realtimeRefreshRef.current)

    // Redirect with optional reason
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : ''
    router.push(`/login${query}`)
  }, [router, queryClient])

  // ── Cross-window logout ─────────────────────────────────────────────────────
  // Logging out (or a 401-driven redirect) in one window/tab/popout clears
  // jm_user in localStorage, which fires a native `storage` event in every
  // OTHER same-origin window (never the one that made the change — that's
  // the `e.newValue !== null` / `!user` guards below double-protecting
  // against a duplicate backend logout call). Each window's in-memory access
  // token would otherwise keep authenticating fine until it happens to
  // refresh or hit a 401 on its own — this makes the other windows notice
  // immediately instead of lagging behind.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== USER_KEY || e.newValue !== null || !user) return
      queryClient.clear()
      setAccessToken(null)
      setUser(null)
      clearRealtimeConfig()
      rocketChat.disconnectWebSocket()
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      if (realtimeRefreshRef.current) clearInterval(realtimeRefreshRef.current)
      router.push('/login')
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [user, queryClient, router])

  // ── Token refresh ───────────────────────────────────────────────────────────
  // Perform the refresh now. Returns true on success. Used by both the scheduled
  // timer and the on-focus handler below. QA WEB-003: the actual network call
  // + single-flight de-dupe lives in api.ts's `refreshAccessToken()` (cookie-
  // based — no refresh token ever passes through this file). It also sets
  // `jm_expires_at` on success, which `scheduleRefresh()` below reads.
  // Retries up to 3 times (delays: 5s, 10s) before giving up — handles transient
  // backend restarts and Cloudflare tunnel blips without logging the user out.
  const refreshNow = useCallback(async (attempt = 0): Promise<boolean> => {
    const ok = await refreshAccessToken()
    if (ok) return true
    // Single-session mode (Rick, 2026-09-24): this session was killed because
    // the account signed in elsewhere — retrying the refresh can't possibly
    // succeed (the server already knows this exact session is dead), so skip
    // the backoff/retry loop and go straight to logout with the specific
    // message instead of the generic "session expired" one.
    if (getLastRefreshErrorCode() === 'SIGNED_IN_ELSEWHERE') {
      logout('signed_in_elsewhere')
      return false
    }
    // Dunning lockout (2026-10-12): the subscription payment failed past the grace period; the server refuses to
    // refresh. Retrying cannot help - end the session and show why on the login page.
    if (getLastRefreshErrorCode() === 'BILLING_SUSPENDED') {
      logout('billing_suspended')
      return false
    }
    if (attempt < 2 && getLastRefreshErrorCode() !== 'NO_REFRESH_TOKEN') {
      await new Promise(r => setTimeout(r, (attempt + 1) * 5_000))
      return refreshNow(attempt + 1)
    }
    logout('session_expired')
    return false
  }, [logout])

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)

    const expiresAt = localStorage.getItem(EXPIRES_KEY)
    if (!expiresAt) return

    const msUntilExpiry = new Date(expiresAt).getTime() - Date.now()
    const refreshIn = Math.max(msUntilExpiry - REFRESH_BUFFER_MS, 5000) // at least 5s

    refreshTimerRef.current = setTimeout(async () => {
      const ok = await refreshNow()
      if (ok) scheduleRefresh() // re-schedule for next refresh
    }, refreshIn)
  }, [refreshNow])

  // ── Load timeout settings from company + team member ───────────────────────
  const loadTimeoutSettings = useCallback(async (teamMemberId?: string | null) => {
    try {
      // Shares the ['company'] cache with every useCompany() caller — returns
      // the cached value when still fresh instead of always issuing a new
      // GET /api/company (QA WEB-002).
      const company = await queryClient.fetchQuery(companyQueryOptions)
      const minutes = company?.settings?.autoTimeoutMinutes
      if (typeof minutes === 'number' && minutes >= 15 && minutes <= 240) {
        idleTimeoutMs.current = minutes * 60_000
        // Clear any idle timer that was set before settings loaded (used the 60-min default).
        // The next user activity will start a fresh timer with the correct duration.
        if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
        if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null }
      }
    } catch { /* company fetch failed — keep default */ }

    if (teamMemberId) {
      try {
        // Shares the ['user', teamMemberId, 'self'] cache with every
        // useCurrentTeamMember() caller (e.g. useAllowJournaling) instead of
        // always issuing a new GET /api/users/{id} (QA WEB-002).
        const member = await queryClient.fetchQuery(currentTeamMemberQueryOptions(teamMemberId))
        if (member?.uiPreferences?.doNotAutoTimeout) {
          // Store flag so resetIdleTimer can check it without a network call
          localStorage.setItem('jm_no_auto_timeout', '1')
          // Clear any idle timer that started before the flag was loaded (race condition fix)
          if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
          if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
        } else {
          localStorage.removeItem('jm_no_auto_timeout')
        }
      } catch { /* keep existing flag */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Idle timeout ────────────────────────────────────────────────────────────
  // Every popout window (see (fullscreen)/layout.tsx) mounts its own AuthProvider,
  // so it tracks activity independently of the main window. Working continuously
  // inside a popout while the main window sits untouched used to let the main
  // window's timer expire and wipe the shared localStorage token — logging the
  // user out even though they were actively working the whole time, just not in
  // the main window. activityChannelRef broadcasts local activity to every other
  // same-origin window (main + all popouts) so they all reset together.
  const lastResetRef = useRef(0)
  const activityChannelRef = useRef<BroadcastChannel | null>(null)
  const resetIdleTimer = useCallback((force = false, fromBroadcast = false) => {
    // Don't track idle if admin mode is on
    if (localStorage.getItem(ADMIN_MODE_KEY) === 'true') return
    // Don't track idle if this user has opted out
    if (localStorage.getItem('jm_no_auto_timeout') === '1') return
    if (!getAccessToken()) return

    // Throttle ONLY the high-frequency continuous events (mousemove/scroll) to avoid
    // timer churn. Discrete actions — clicks, keypresses, navigation — pass force=true
    // so they ALWAYS reset (this is why "clicking a nav button didn't reset the timer":
    // a click landing within 15s of a mousemove was being throttled away).
    const now = Date.now()
    if (!force && now - lastResetRef.current < 15_000) return
    lastResetRef.current = now

    // Tell every other same-origin window (main + popouts) about this real
    // activity so their independent idle timers reset too. Don't re-broadcast
    // a reset that itself came from a broadcast — that would ping-pong.
    if (!fromBroadcast) activityChannelRef.current?.postMessage('activity')

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)

    // Proactively extend the JWT on activity (sliding session)
    scheduleRefresh()

    // Main idle timer → show warning
    idleTimerRef.current = setTimeout(() => {
      // Show a warning toast — emit a custom event the Navbar/layout can listen to
      window.dispatchEvent(new CustomEvent('jm:idle-warning'))

      // After warning period → auto-logout
      warningTimerRef.current = setTimeout(() => {
        logout('idle_timeout')
      }, IDLE_WARNING_MS)
    }, idleTimeoutMs.current - IDLE_WARNING_MS)
  }, [logout, scheduleRefresh])

  // ── Mount: remember-me check — must run before session-restore ────────────
  // Moving this out of the render body prevents React Strict Mode from
  // running the side effect twice and unexpectedly clearing tokens.
  useEffect(() => {
    const rememberMe = localStorage.getItem('jm_remember_me')
    if (rememberMe === '0' && !sessionStorage.getItem('jm_active_session')) {
      setAccessToken(null)
      localStorage.removeItem(USER_KEY)
      localStorage.removeItem(EXPIRES_KEY)
      localStorage.removeItem(LEGACY_TOKEN_KEY)
      localStorage.removeItem(LEGACY_REFRESH_KEY)
      localStorage.removeItem('jm_remember_me')
    }
    sessionStorage.setItem('jm_active_session', '1')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Mount: restore session ──────────────────────────────────────────────────
  // Circuit-breaker: prevent infinite re-mount loops (guards against useEffect dep changes)
  const sessionRestoredRef = useRef(false)

  useEffect(() => {
    // Guard: only run session restore ONCE per component lifecycle
    if (sessionRestoredRef.current) return
    sessionRestoredRef.current = true

    const stored = localStorage.getItem(USER_KEY)
    const adminMode = localStorage.getItem(ADMIN_MODE_KEY)

    if (adminMode === 'true') setAdminModeRaw(true)

    if (!stored) { setIsLoading(false); return }

    // QA WEB-003: the access token is never persisted, so there's no local
    // JWT to inspect for expiry — this window (fresh load, reload, or
    // popout) always has to mint a fresh one from the httpOnly refresh
    // cookie. `refreshAccessToken()` is single-flight and also handles the
    // one-time legacy-localStorage-refresh-token migration (item 8), so a
    // pre-deploy session lands here exactly like a normal cookie restore.
    //
    // Retries with the same backoff as refreshNow() below: a refresh token
    // is single-use (rotated on every exchange), so two windows/tabs booting
    // within the same instant — or a dev-only Fast Refresh remount replaying
    // this effect — can race for the one cookie value; the loser gets a
    // legitimate-but-transient "Invalid refresh token" the very first time,
    // and the browser already holds the winner's rotated cookie by the next
    // attempt. Without a retry here, that one race silently logs the user
    // out on the very next tab/reload instead of self-healing (observed
    // during QA WEB-003 verification, 2026-09-18).
    async function bootRefreshWithRetry(attempt = 0): Promise<boolean> {
      const ok = await refreshAccessToken()
      if (ok || attempt >= 2) return ok
      // No cookie at all (pre-cookie session, or logged out elsewhere): a
      // retry cannot succeed — go straight to the logged-out path instead of
      // ~30s of spinner (Tony, 2026-09-18).
      if (getLastRefreshErrorCode() === 'NO_REFRESH_TOKEN') return false
      if (getLastRefreshErrorCode() === 'BILLING_SUSPENDED') return false // dunning lockout: a retry cannot succeed
      await new Promise(r => setTimeout(r, (attempt + 1) * 5_000))
      return bootRefreshWithRetry(attempt + 1)
    }

    bootRefreshWithRetry().then(ok => {
      if (!ok) {
        // No valid cookie (or migration failed) — session is genuinely gone.
        localStorage.removeItem(USER_KEY)
        localStorage.removeItem(EXPIRES_KEY)
        setIsLoading(false)
        // Dunning lockout (2026-10-12): tell the user WHY they are on the login page.
        if (getLastRefreshErrorCode() === 'BILLING_SUSPENDED' && window.location.pathname !== '/login') {
          window.location.replace('/login?reason=billing_suspended')
        }
        return
      }
      try {
        const parsed = JSON.parse(stored)
        setUser(parsed)
        // Load company timeout + team member opt-out on session restore (non-blocking)
        loadTimeoutSettings(parsed.teamMemberId).catch(() => {})
        // Refresh realtime client config (non-blocking; supabase.ts already loaded
        // the saved config from localStorage, this keeps the token fresh)
        initRealtimeClient().catch(() => {})
        if (realtimeRefreshRef.current) clearInterval(realtimeRefreshRef.current)
        realtimeRefreshRef.current = setInterval(() => { initRealtimeClient().catch(() => {}) }, 50 * 60_000)
        // Restore RC session on page load
        initRocketChat(parsed.teamMemberId).catch(() => {})
      } catch {
        localStorage.removeItem(USER_KEY)
      }
      scheduleRefresh()
      setIsLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Cross-window activity sync (main window + every popout) ────────────────
  useEffect(() => {
    if (!user || typeof BroadcastChannel === 'undefined') return
    const ch = new BroadcastChannel('jm_activity')
    activityChannelRef.current = ch
    ch.onmessage = () => resetIdleTimer(true, true)
    return () => { ch.close(); activityChannelRef.current = null }
  }, [user, resetIdleTimer])

  // ── Attach idle listeners ───────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return
    // Continuous (high-frequency) events are throttled; discrete user actions force a
    // reset so a click/keypress always counts. (Wrappers are required so the DOM event
    // object isn't passed as the `force` argument.)
    const onContinuous = () => resetIdleTimer(false)
    const onDiscrete = () => resetIdleTimer(true)
    const continuous = ['mousemove', 'scroll', 'touchstart'] as const
    const discrete = ['mousedown', 'pointerdown', 'keydown', 'click'] as const
    continuous.forEach(e => window.addEventListener(e, onContinuous, { passive: true }))
    discrete.forEach(e => window.addEventListener(e, onDiscrete, { passive: true }))
    resetIdleTimer(true)
    return () => {
      continuous.forEach(e => window.removeEventListener(e, onContinuous))
      discrete.forEach(e => window.removeEventListener(e, onDiscrete))
    }
  }, [user, resetIdleTimer])

  // ── Any navigation resets the idle countdown ────────────────────────────────
  // Clicking ANY nav item changes the route, so this guarantees a reset even if the
  // click's DOM event was captured/stopped before bubbling to window (e.g. a menu
  // item that calls stopPropagation). Directly addresses "left-nav clicks should reset".
  const pathname = usePathname()
  useEffect(() => {
    if (user) resetIdleTimer(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // ── Refresh on tab focus / visibility ────────────────────────────────────────
  // Browsers throttle long setTimeouts in background tabs, so the scheduled
  // refresh above can fire late and the access token lapses while the tab sits
  // idle. When the user returns to the tab, refresh immediately if the token is
  // at/near expiry (then re-arm the timer); otherwise just re-arm in case the
  // background timer was throttled. This is the root-cause fix for "data went
  // empty after ~1h, F5 fixes it".
  useEffect(() => {
    if (!user) return
    const onFocus = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      const expiresAt = localStorage.getItem(EXPIRES_KEY)
      if (!expiresAt) return
      const msUntilExpiry = new Date(expiresAt).getTime() - Date.now()
      if (msUntilExpiry <= REFRESH_BUFFER_MS) {
        refreshNow().then(ok => { if (ok) scheduleRefresh() })
      } else {
        scheduleRefresh()
      }
    }
    document.addEventListener('visibilitychange', onFocus)
    window.addEventListener('focus', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onFocus)
      window.removeEventListener('focus', onFocus)
    }
  }, [user, refreshNow, scheduleRefresh])

  // ── Login ───────────────────────────────────────────────────────────────────
  const login = async (email: string, password: string, rememberMe = true, turnstileToken?: string) => {
    // QA WEB-003: api.ts's request() tags /api/mvp-auth/login with
    // credentials:'include' + x-jm-client:'web' automatically — the backend
    // responds by setting the httpOnly jm_refresh cookie and OMITTING
    // refreshToken from this body entirely, so it never touches the browser.
    // WP-11 addition (Turnstile, 2026-09-23): turnstileToken is omitted
    // entirely (never sent as '' or undefined) when the widget hasn't
    // produced one yet — the backend only requires it for web clients in
    // TURNSTILE_LOGIN_MODE=enforce; every other mode ignores its absence.
    const res = await api.post<{
      token: string
      expiresAt: string
      user: AuthUser
    }>('/api/mvp-auth/login', { email, password, ...(turnstileToken ? { turnstileToken } : {}) })

    // Account switch: wipe any cached data from a PREVIOUS user/tenant BEFORE
    // the new session renders, so a different tenant's clients/jobs/company can
    // never bleed through into this login (e.g. logging in as a Dev-tenant user
    // while a T001 user's data is still cached). logout() already clears, but a
    // direct re-login without logout would otherwise keep the stale cache.
    queryClient.clear()

    setAccessToken(res.token)
    localStorage.setItem(EXPIRES_KEY, res.expiresAt)
    localStorage.setItem(USER_KEY, JSON.stringify(res.user))
    // Remember Me: store preference so AuthProvider can clear tokens on browser restart
    localStorage.setItem('jm_remember_me', rememberMe ? '1' : '0')
    setUser(res.user)
    scheduleRefresh()

    // NOTE: the mandatory "Set Your Password" prompt is intentionally disabled.
    // Onboarding now sets the real password via the emailed set-password link
    // (the canonical mechanism — see commit 1139a390), so forcing an in-app
    // change after login was redundant and fired even for users who had already
    // set their password. Manual password changes remain available from the
    // team-member detail screen.

    // Load company timeout setting + team member opt-out flag (non-blocking)
    loadTimeoutSettings(res.user.teamMemberId).catch(() => {})

    // Swap the Supabase realtime client to the tenant's own project (non-blocking)
    initRealtimeClient().catch(() => {})
    if (realtimeRefreshRef.current) clearInterval(realtimeRefreshRef.current)
    realtimeRefreshRef.current = setInterval(() => { initRealtimeClient().catch(() => {}) }, 50 * 60_000)

    // Initialize Rocket.Chat silently (non-blocking)
    initRocketChat(res.user.teamMemberId).catch(() => {})
  }

  /** Fetch per-tenant realtime config and swap the Supabase client. Fire-and-forget. */
  const initRealtimeClient = useCallback(async () => {
    try {
      const cfg = await api.get<{ supabaseUrl: string; supabaseAnonKey: string; realtimeToken?: string }>(
        '/api/realtime-config'
      )
      if (cfg?.supabaseUrl && cfg?.supabaseAnonKey) {
        reinitSupabase(cfg.supabaseUrl, cfg.supabaseAnonKey, cfg.realtimeToken)
      }
    } catch {
      // Non-fatal — realtime falls back to whatever client was already active
    }
  }, [])

  /** Fetch RC token and connect WebSocket — called after login and on session restore */
  const initRocketChat = async (teamMemberId?: string | null) => {
    if (!teamMemberId) return
    try {
      const rcUrl = process.env.NEXT_PUBLIC_ROCKETCHAT_URL || process.env.NEXT_PUBLIC_BACKEND_URL?.replace(/:\d+$/, ':3000') || ''
      if (!rocketChat.isConfigured() && rcUrl) {
        rocketChat.configure(rcUrl)
      }
      // Check for stored RC session first
      const storedToken = localStorage.getItem('rc_authToken')
      const storedUserId = localStorage.getItem('rc_userId')
      if (storedToken && storedUserId) {
        rocketChat.restoreSession(storedToken, storedUserId)
        rocketChat.connectWebSocket()
        return
      }
      // Fetch fresh token from backend
      // Automatic, not a user action: on a plan without Team Chat this answers 402 and must stay silent
      // (it used to open the upgrade dialog on every page load for an Essentials account).
      const rc = await api.postBackground<{ authToken: string; userId: string; rcUrl: string; wsUrl: string }>(
        '/api/chat/token',
        { teamMemberId }
      )
      if (rc.authToken) {
        if (!rocketChat.isConfigured()) rocketChat.configure(rc.rcUrl)
        rocketChat.restoreSession(rc.authToken, rc.userId)
        localStorage.setItem('rc_authToken', rc.authToken)
        localStorage.setItem('rc_userId', rc.userId)
        rocketChat.connectWebSocket()
        setChatUnavailable(null)
      }
    } catch (err: any) {
      // RC not available — chat just won't work, app is fine. A classified
      // CHAT_UNAVAILABLE 503 (api.ts) is an RC-side outage, not a bug — record
      // the reason so ChatWindow can show its one-time friendly notice. Never
      // retried here; the next attempt is whatever next calls initRocketChat
      // (next login/restore), not a loop.
      if (err?.code === 'CHAT_UNAVAILABLE') {
        setChatUnavailable(err.reason ?? null)
      }
    }
  }

  const isAuthenticated = !!user

  return (
    <AuthContext.Provider value={{
      user, isLoading, isAuthenticated,
      login, logout,
      adminModeEnabled, setAdminModeEnabled,
    }}>
      {children}
      <ChangePasswordDialog
        // Mandatory set-password prompt disabled — onboarding sets the real
        // password via the emailed set-password link, so this forced dialog was
        // redundant and fired even after the user had already set a password.
        open={false}
        mandatory
        // Prefer the in-memory temp pw; fall back to the sessionStorage copy
        // stashed at login so the new provider can still lock the Current field.
        lockCurrent={
          forceChangePwd ??
          (typeof window !== 'undefined' ? sessionStorage.getItem('jm_temp_pwd') ?? undefined : undefined)
        }
        title='Set Your Password'
        onClose={() => setForceChangePwd(null)}
        onSuccess={() => {
          setForceChangePwd(null)
          try { sessionStorage.removeItem('jm_temp_pwd') } catch { /* ignore */ }
          setUser(u => {
            const next = u ? { ...u, mustChangePassword: false } : u
            // Persist the cleared flag so a refresh doesn't re-open the dialog.
            if (next) { try { localStorage.setItem(USER_KEY, JSON.stringify(next)) } catch { /* ignore */ } }
            return next
          })
        }}
      />
    </AuthContext.Provider>
  )
}

// React 19 concurrent rendering can briefly render a component outside its
// provider during a tear-down/re-render. Throwing here caused recurring user-
// facing crashes (tasks #1050, #1063, #1064) every time a new caller surfaced.
// Returning a safe stub keeps callers working in those transient renders; the
// next commit phase will hand them the real context.
const STUB_AUTH: AuthContextType = {
  user: null,
  isLoading: false,
  isAuthenticated: false,
  login: async () => { /* no-op outside provider */ },
  logout: () => { /* no-op outside provider */ },
  adminModeEnabled: false,
  setAdminModeEnabled: () => { /* no-op outside provider */ },
}

export function useAuth(): AuthContextType {
  return useContext(AuthContext) ?? STUB_AUTH
}

/** Same as useAuth — kept for clarity at call sites that want to be explicit. */
export function useOptionalAuth(): AuthContextType | null {
  return useContext(AuthContext)
}
